/* ──────────────────────────────────────────────────────────────
   AIWorkplaceCongratulations.js  ·  Congratulations page for AI Workplace
   ──────────────────────────────────────────────────────────── */
import React from "react";
import { useNavigate } from "react-router-dom";
import tpcLogo from "../../../images/TPC-logo-light.png";
import "./AIWorkplaceCongratulations.css";

const AIWorkplaceCongratulations = ({ profileData = {} }) => {
  const navigate = useNavigate();

  const handleViewProfile = () => {
    navigate("/ai-workplace-login");
  };

  // Format the selected enrollment date
  const formatDate = (dateString) => {
    if (!dateString) return "To be determined";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format the Friday date
  const formatFridayDate = (dateString) => {
    if (!dateString) return "To be determined";
    const date = new Date(dateString);
    const friday = new Date(date);
    friday.setDate(friday.getDate() + 1);
    return friday.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="aiw-congratulations-page">
      <div className="aiw-congratulations-container">
        {/* Header with Logo */}
        <div className="aiw-congrats-header">
          <img src={tpcLogo} alt="TPC" className="aiw-congrats-logo" />
        </div>

        {/* Main Content Card */}
        <div className="aiw-congrats-card">
          {/* Success Icon */}
          <div className="aiw-success-icon">
            <div className="aiw-checkmark-circle">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <div className="aiw-congrats-title">
            <h1>Application Submitted Successfully</h1>
            <p className="aiw-congrats-subtitle">
              Your AI in the Workplace Journey Begins
            </p>
          </div>

          {/* Welcome Message */}
          <div className="aiw-congrats-message">
            <p>
              Dear <strong>{profileData.fullName || "Applicant"}</strong>,
            </p>
            <p>
              Thank you for your interest in our AI in the Workplace program. We
              have successfully received your application and are reviewing the
              information you provided.
            </p>
            <p>
              Our team will contact you within{" "}
              <strong>2-3 business days</strong> to discuss your personalized
              training plan and confirm your enrollment details.
            </p>
          </div>

          {/* Application Summary */}
          <div className="aiw-summary-section">
            <h3>Application Summary</h3>
            <div className="aiw-summary-grid">
              <div className="aiw-summary-item">
                <span className="aiw-summary-label">Full Name</span>
                <span className="aiw-summary-value">
                  {profileData.fullName || "N/A"}
                </span>
              </div>

              <div className="aiw-summary-item">
                <span className="aiw-summary-label">Email Address</span>
                <span className="aiw-summary-value">
                  {profileData.email || "N/A"}
                </span>
              </div>

              <div className="aiw-summary-item">
                <span className="aiw-summary-label">Company/Organization</span>
                <span className="aiw-summary-value">
                  {profileData.company || "N/A"}
                </span>
              </div>

              <div className="aiw-summary-item">
                <span className="aiw-summary-label">Country</span>
                <span className="aiw-summary-value">
                  {profileData.country || "N/A"}
                </span>
              </div>

              <div className="aiw-summary-item">
                <span className="aiw-summary-label">Application Type</span>
                <span className="aiw-summary-value">
                  {profileData.applicationType || "N/A"}
                  {profileData.applicationType === "Team" &&
                    profileData.teamMemberCount &&
                    ` (${profileData.teamMemberCount} members)`}
                </span>
              </div>

              <div className="aiw-summary-item">
                <span className="aiw-summary-label">AI Experience Level</span>
                <span className="aiw-summary-value">
                  {profileData.aiExperienceLevel || "N/A"}
                </span>
              </div>

              <div className="aiw-summary-item">
                <span className="aiw-summary-label">Industry Sector</span>
                <span className="aiw-summary-value">
                  {profileData.workplaceSector || "N/A"}
                </span>
              </div>

              <div className="aiw-summary-item">
                <span className="aiw-summary-label">
                  Selected Enrollment Date
                </span>
                <span className="aiw-summary-value">
                  {profileData.selectedThursdays &&
                  profileData.selectedThursdays.length > 0 ? (
                    <>
                      {formatDate(profileData.selectedThursdays[0])}
                      <br />
                      <small>
                        & {formatFridayDate(profileData.selectedThursdays[0])}
                      </small>
                    </>
                  ) : (
                    "To be determined"
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="aiw-contact-section">
            <div className="aiw-contact-card">
              <h4>Need Assistance?</h4>
              <p>
                If you have any questions or need to make changes to your
                application, please contact us at:
              </p>
              <a
                href="mailto:registrations@tpc.training"
                className="aiw-contact-email"
              >
                registrations@tpc.training
              </a>
            </div>
          </div>

          {/* Action Button */}
          <div className="aiw-congrats-actions">
            <button onClick={handleViewProfile} className="aiw-btn-primary">
              Proceed to Sign In
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="aiw-congrats-footer">
          <p>© 2025 Tech Performance Centre. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default AIWorkplaceCongratulations;
